import { CarrierContact } from 'types/CarrierVerification';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { Flex } from '../layout';
import { Typography } from '../typography';

interface EmailOption {
  email: string;
  contact?: CarrierContact;
}

interface CarrierVerificationInviteDropdownProps {
  contacts: CarrierContact[];
  allEmails?: string[];
  inviteButton: React.ReactNode;
  onEmailSelect: (email: string, contact?: CarrierContact) => void;
  formatContactName: (contact: CarrierContact) => string;
}

export default function CarrierVerificationInviteDropdown({
  contacts,
  allEmails = [],
  inviteButton,
  onEmailSelect,
  formatContactName,
}: CarrierVerificationInviteDropdownProps) {
  // Combine contacts and all_emails with case-insensitive deduplication
  const emailOptions: EmailOption[] = [];
  const seenEmails = new Set<string>();

  // First, add contacts (they have more info)
  contacts.forEach((contact) => {
    const emailLower = contact.email.toLowerCase();
    if (!seenEmails.has(emailLower)) {
      seenEmails.add(emailLower);
      emailOptions.push({ email: contact.email, contact });
    }
  });

  // Then add any emails from all_emails that aren't already in contacts
  allEmails.forEach((email) => {
    const emailLower = email.toLowerCase();
    if (!seenEmails.has(emailLower)) {
      seenEmails.add(emailLower);
      emailOptions.push({ email });
    }
  });

  if (emailOptions.length === 0) {
    return <>{inviteButton}</>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{inviteButton}</DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-64'>
        {emailOptions.map((option, index) => (
          <DropdownMenuItem
            key={index}
            onClick={() => {
              onEmailSelect(option.email, option.contact);
            }}
            className='cursor-pointer'
          >
            <Flex direction='col' align='start' gap='xs' className='w-full'>
              {option.contact ? (
                <>
                  <Typography variant='body-sm' className='truncate w-full'>
                    {formatContactName(option.contact)}
                  </Typography>
                  {option.contact.title && (
                    <Typography
                      variant='body-xs'
                      textColor='muted'
                      className='truncate w-full'
                    >
                      {option.contact.title}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant='body-sm' className='truncate w-full'>
                  {option.email}
                </Typography>
              )}
            </Flex>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
